// web/geneInventory.ts — the GENE INVENTORY as a first-class progression view.
//
// One screen to make managing loot effortless: every held gene, grouped by the CASTE
// it suits, with sorting (Power / Rarity / Newest / Upgradeable / Duplicates), a single
// search box (name · trait · Power Score), and status filters (Equipped / Unequipped /
// Upgradeable). Pure + self-contained like armory.ts / advisor.ts: it reasons over the
// already-computed `GeneRowView`s (which carry power, rarity, caste-fit, ascension) plus
// the player's `applyGene` log (the genome stacks applied genes → the log IS the equipped
// set). It NEVER touches the sim, the save, determinism, or any hash — a render-only
// read-model. Actions fired from the cards reuse the existing replay-safe session methods.

import type { GeneRowView } from './geneProgression.ts';
import type { AppliedGene } from './armory.ts';
import { primaryCasteForType } from '../src/sim/casteGenes.ts';
import { RARITY_NAME } from '../src/sim/geneData.ts';
import { rarityPerk } from '../src/sim/rarityPerks.ts';
import {
  classifyShare, TARGET_SHARE_LO_PCT, TARGET_SHARE_HI_PCT, MAX_SHARE_PCT,
} from '../src/sim/slotBudget.ts';
import {
  rarityBandHTML, statLinesHTML, generationHTML, powerHTML,
} from './geneCard.ts';

// --- modes -------------------------------------------------------------------

export const INV_SORT = {
  Power: 'power',
  Rarity: 'rarity',
  Newest: 'newest',
  Upgradeable: 'upgradeable',
  Duplicates: 'duplicates',
} as const;
export type InvSort = (typeof INV_SORT)[keyof typeof INV_SORT];

export const INV_SORT_LABEL: Readonly<Record<InvSort, string>> = {
  power: 'Power', rarity: 'Rarity', newest: 'Newest', upgradeable: 'Upgradeable', duplicates: 'Duplicates',
};

export const INV_FILTER = {
  Equipped: 'equipped',
  Unequipped: 'unequipped',
  Upgradeable: 'upgradeable',
} as const;
export type InvFilter = (typeof INV_FILTER)[keyof typeof INV_FILTER];

/** -1 = "All castes"; 0..3 = the four caste categories. */
export const INV_CATEGORY_ALL = -1;

// --- view model --------------------------------------------------------------

export interface InventoryItem extends GeneRowView {
  /** Another held gene shares this gene's (type, rarity) — a fusion/sacrifice candidate. */
  isDuplicate: boolean;
  /** A gene matching this one (type+rarity+mutations) has been applied to a caste. */
  isEquipped: boolean;
  /** Can be ascended up its named ladder (a progression upgrade). */
  isUpgradeable: boolean;
  /** This gene's (type, rarity) is on the player's Evolution Wishlist. */
  isWishlisted: boolean;
}

export interface InventoryOpts {
  /** Caste category filter (INV_CATEGORY_ALL or 0..3). */
  caste: number;
  sort: InvSort;
  /** Free-text search over name / trait / Power Score (case-insensitive). */
  query: string;
  /** Active status filters; empty ⇒ show all. An item passes if it matches ANY. */
  filters: readonly InvFilter[];
  /**
   * Evolution Wishlist target keys (`type:rarity`, see web/wishlist.ts). A gene whose
   * own (type, rarity) is on the list renders a filled ★ toggle + badge. Optional and
   * render-only — purely a player preference, never sim/save state.
   */
  wishlist?: ReadonlySet<string>;
}

export interface InventoryView {
  items: InventoryItem[];
  /** Held genes per caste category (over the search-filtered set) — for the tab badges. */
  byCaste: number[];
  /** Total held (search-filtered), and per status (caste+search filtered) — for chips. */
  counts: { all: number; equipped: number; unequipped: number; upgradeable: number };
}

// --- pure logic --------------------------------------------------------------

/** Value-identity signatures of every applied gene: `type:rarity:mutations`. The log is
 *  by value (no gene id), so this is the most specific equipped-match available. */
function equippedSignatures(applied: readonly AppliedGene[]): Set<string> {
  const out = new Set<string>();
  for (const a of applied) out.add(`${a.type}:${a.rarity}:${a.traits.length}`);
  return out;
}

/** True when `q` (already lowercased, non-empty) hits the gene's name, a trait, its Power
 *  Score, rarity name, or caste label. `label` already embeds the trait + type names. */
function matchesQuery(item: InventoryItem, q: string): boolean {
  if (item.label.toLowerCase().includes(q)) return true;
  if (String(item.power).includes(q)) return true;
  if ((RARITY_NAME[item.rarity] ?? '').toLowerCase().includes(q)) return true;
  if (item.casteLabel.toLowerCase().includes(q)) return true;
  return false;
}

/** Deterministic comparator for a sort mode. Ties break on serial (stable, newest-first). */
function comparator(sort: InvSort): (a: InventoryItem, b: InventoryItem) => number {
  switch (sort) {
    case INV_SORT.Rarity:
      return (a, b) => b.rarity - a.rarity || b.power - a.power || b.serial - a.serial;
    case INV_SORT.Newest:
      return (a, b) => b.serial - a.serial;
    case INV_SORT.Upgradeable:
      return (a, b) => Number(b.isUpgradeable) - Number(a.isUpgradeable) || b.power - a.power || b.serial - a.serial;
    case INV_SORT.Duplicates:
      // Duplicates first, clustered by (type, rarity) so identical genes sit together.
      return (a, b) =>
        Number(b.isDuplicate) - Number(a.isDuplicate) || a.type - b.type || b.rarity - a.rarity || b.power - a.power;
    case INV_SORT.Power:
    default:
      return (a, b) => b.power - a.power || b.serial - a.serial;
  }
}

function passesFilters(item: InventoryItem, filters: readonly InvFilter[]): boolean {
  if (filters.length === 0) return true;
  for (const f of filters) {
    if (f === INV_FILTER.Equipped && item.isEquipped) return true;
    if (f === INV_FILTER.Unequipped && !item.isEquipped) return true;
    if (f === INV_FILTER.Upgradeable && item.isUpgradeable) return true;
  }
  return false;
}

/**
 * Build the inventory view: enrich each row (duplicate / equipped / upgradeable), then
 * category-filter (caste), search-filter, status-filter, and sort. Pure: same inputs ⇒
 * same view. `byCaste` + `counts` are computed BEFORE the caste/status narrowing so the
 * tabs and chips show how many genes each choice would reveal.
 */
export function buildInventory(
  rows: readonly GeneRowView[],
  applied: readonly AppliedGene[],
  opts: InventoryOpts,
): InventoryView {
  const equipped = equippedSignatures(applied);

  // Duplicate detection: count held genes per (type, rarity).
  const dupKey = (r: GeneRowView): string => `${r.type}:${r.rarity}`;
  const byKey = new Map<string, number>();
  for (const r of rows) byKey.set(dupKey(r), (byKey.get(dupKey(r)) ?? 0) + 1);

  const wish = opts.wishlist;
  const enriched: InventoryItem[] = rows.map((r) => ({
    ...r,
    isDuplicate: (byKey.get(dupKey(r)) ?? 0) > 1,
    isEquipped: equipped.has(`${r.type}:${r.rarity}:${r.traitTotal}`),
    isUpgradeable: r.canAscend,
    isWishlisted: wish ? wish.has(`${r.type}:${r.rarity}`) : false,
  }));

  const q = opts.query.trim().toLowerCase();
  const searched = q === '' ? enriched : enriched.filter((it) => matchesQuery(it, q));

  // Tab badges: per-caste counts over the searched set (a gene counts for every caste it
  // suits, so a hybrid shows under both relevant tabs).
  const byCaste = [0, 0, 0, 0];
  for (const it of searched) for (const c of it.castes) if (c >= 0 && c < 4) byCaste[c] = (byCaste[c] as number) + 1;

  const inCaste = opts.caste === INV_CATEGORY_ALL
    ? searched
    : searched.filter((it) => it.castes.includes(opts.caste));

  const counts = {
    all: inCaste.length,
    equipped: inCaste.filter((it) => it.isEquipped).length,
    unequipped: inCaste.filter((it) => !it.isEquipped).length,
    upgradeable: inCaste.filter((it) => it.isUpgradeable).length,
  };

  const items = inCaste.filter((it) => passesFilters(it, opts.filters)).sort(comparator(opts.sort));
  return { items, byCaste, counts };
}

// --- view (HTML) -------------------------------------------------------------

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;'));
}

/** The toolbar: caste tabs, sort select, search box, status chips. */
export function inventoryToolbarHTML(opts: InventoryOpts, view: InventoryView, casteNames: readonly string[]): string {
  const total = view.byCaste.reduce((a, b) => a + b, 0);
  const tab = (c: number, label: string, n: number): string =>
    `<button class="inv-tab${opts.caste === c ? ' on' : ''}" data-inv-caste="${c}">${esc(label)} <span class="inv-n">${n}</span></button>`;
  const tabs =
    tab(INV_CATEGORY_ALL, 'All', total) +
    casteNames.slice(0, 4).map((n, i) => tab(i, `${n}s`, view.byCaste[i] as number)).join('');

  const sortOpts = (Object.values(INV_SORT) as InvSort[])
    .map((s) => `<option value="${s}"${opts.sort === s ? ' selected' : ''}>${INV_SORT_LABEL[s]}</option>`)
    .join('');

  const chip = (f: InvFilter, label: string, n: number): string =>
    `<button class="inv-chip${opts.filters.includes(f) ? ' on' : ''}" data-inv-filter="${f}">${label} <span class="inv-n">${n}</span></button>`;
  const chips =
    chip(INV_FILTER.Equipped, 'Equipped', view.counts.equipped) +
    chip(INV_FILTER.Unequipped, 'Unequipped', view.counts.unequipped) +
    chip(INV_FILTER.Upgradeable, 'Upgradeable', view.counts.upgradeable);

  return (
    `<div class="inv-tabs">${tabs}</div>` +
    `<div class="inv-controls">` +
      `<input id="inv-search" class="inv-search" type="text" placeholder="Search name · trait · power…" value="${esc(opts.query)}" autocomplete="off" />` +
      `<label class="inv-sortwrap">Sort <select id="inv-sort" class="inv-sort">${sortOpts}</select></label>` +
      `<div class="inv-chips">${chips}</div>` +
    `</div>`
  );
}

/**
 * One COLLECTIBLE gene card (the unified loot look, shared with the box reveal via
 * web/geneCard.ts): rarity band · name · "<Caste> Gene" · Power N · the human +N% stat
 * lines · Generation N · the actions ([Equip] [Sacrifice], plus Ascend / wishlist). No
 * raw modifier strings or internal integers are ever shown — ids stay in data-* only.
 * `generation` is the colony's current generation (context); omit to hide the line.
 */
function cardHTML(it: InventoryItem, casteNames: readonly string[], generation?: number): string {
  const equipCaste = casteNames[primaryCasteForType(it.type)] ?? 'caste';
  const badges =
    (it.isEquipped ? '<span class="inv-badge eq">Equipped</span>' : '<span class="inv-badge un">Unequipped</span>') +
    (it.isUpgradeable ? '<span class="inv-badge up">Upgradeable</span>' : '') +
    (it.isDuplicate ? '<span class="inv-badge dup">Duplicate</span>' : '') +
    (it.isWishlisted ? '<span class="inv-badge wish">★ Wishlist</span>' : '');
  const ascend = it.canAscend
    ? `<button class="btn small inv-ascend" data-inv-ascend="${it.serial}" title="Evolve along its named ladder">Ascend ${it.ascendCost}⚡</button>`
    : '';
  // Star toggles THIS gene's (type, rarity) on the Evolution Wishlist.
  const wishBtn =
    `<button class="inv-wish${it.isWishlisted ? ' on' : ''}" data-wish-toggle="${it.type}:${it.rarity}" type="button" ` +
      `aria-pressed="${it.isWishlisted}" ` +
      `title="${it.isWishlisted ? 'On your wishlist — click to remove' : 'Add this gene to your Evolution Wishlist'}">` +
      `${it.isWishlisted ? '★' : '☆'}</button>`;
  const pk = rarityPerk(it.type, it.rarity);
  const perk = pk ? `<div class="inv-perk${pk.special ? ' special' : ''}"><b>${esc(pk.name)}</b> — ${esc(pk.effect)}</div>` : '';
  let gain = '';
  if (typeof it.gainPct === 'number' && it.gainPct > 0) {
    const pct = Math.round(it.gainPct);
    const cls = classifyShare(pct); // slot-budget band: minor / on-target / heavy / dominant
    const note = cls === 'dominant' ? ' · ⚠ dominates the caste'
      : cls === 'heavy' ? ' · heavy'
      : cls === 'on-target' ? ' · on budget'
      : '';
    const title = `Share of ${esc(equipCaste)} power if equipped now — aim ${TARGET_SHARE_LO_PCT}–${TARGET_SHARE_HI_PCT}% per gene; over ${MAX_SHARE_PCT}% is overpowering`;
    gain = `<div class="inv-gain share-${cls}" title="${title}">≈ +${pct}% ${esc(equipCaste)} power${note}</div>`;
  }
  return (
    `<div class="inv-card gc-card rcol-${it.rarity}">` +
      `<div class="inv-row1">${rarityBandHTML(it.rarity)}` +
      `<span class="inv-row1-right">${wishBtn}${powerHTML(it.power)}</span></div>` +
      `<div class="gc-name">${esc(it.label)}</div>` +
      `<div class="inv-row2"><span class="gc-caste">${esc(equipCaste)} Gene</span>` +
      `<span class="inv-fit muted" title="Castes this body part suits">${esc(it.casteLabel)}</span></div>` +
      statLinesHTML(it.type, it.rarity, it.traitTotal) +
      generationHTML(generation) +
      gain +
      perk +
      `<div class="inv-badges">${badges}</div>` +
      `<div class="inv-actions">` +
        `<button class="btn small btn-primary inv-apply" data-inv-apply="${it.id}" data-inv-type="${it.type}" title="Apply to your ${esc(equipCaste)}s now">Equip → ${esc(equipCaste)}</button>` +
        ascend +
        `<button class="btn small inv-sacrifice" data-inv-sacrifice="${it.serial}" title="Break this gene down into Mutation Energy">Sacrifice</button>` +
      `</div>` +
    `</div>`
  );
}

/** Just the result cards — re-rendered on every keystroke into `#inv-grid` without
 *  disturbing (or re-focusing) the toolbar's search box. `generation` is the colony's
 *  current generation, shown as context on each card (optional). */
export function inventoryGridHTML(view: InventoryView, casteNames: readonly string[], generation?: number): string {
  if (view.items.length === 0) {
    return '<div class="inv-empty">No genes match — try another category, clear the search, or open an Evolution Box.</div>';
  }
  return view.items.map((it) => cardHTML(it, casteNames, generation)).join('');
}

/** The whole screen body: toolbar + the `#inv-grid` results container. */
export function inventoryBodyHTML(opts: InventoryOpts, view: InventoryView, casteNames: readonly string[], generation?: number): string {
  return (
    inventoryToolbarHTML(opts, view, casteNames) +
    `<div id="inv-grid" class="inv-grid">${inventoryGridHTML(view, casteNames, generation)}</div>`
  );
}
